import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Tooltip,
  Fab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  LinearProgress,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  CardHeader,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  FilterList,
  Refresh,
  Visibility,
  Download,
  Print,
  ExpandMore,
  Factory,
  Engineering,
  Settings,
  PlayArrow,
  Pause,
  Stop,
  CheckCircle,
  Warning,
  Error,
  TrendingUp,
  TrendingDown,
  Business,
  Assessment,
  Schedule,
  Speed,
  Timer,
} from '@mui/icons-material';
import { useRbac } from '../../context/RbacContext';

interface ProductionLine {
  id: string;
  name: string;
  type: 'fabric' | 'furniture' | 'metalworks' | 'shoes';
  status: 'active' | 'inactive' | 'maintenance' | 'breakdown';
  capacity: number;
  currentCapacity: number;
  efficiency: number;
  managerId: string;
  managerName: string;
  location: string;
  description: string;
  isActive: boolean;
  createdAt: string;
  lastMaintenance: string;
  nextMaintenance: string;
  machines: Machine[];
  currentOrder?: ProductionOrder;
}

interface Machine {
  id: string;
  name: string;
  model: string;
  type: string;
  status: 'operational' | 'maintenance' | 'breakdown' | 'idle';
  capacity: number;
  efficiency: number;
  lastMaintenance: string;
  nextMaintenance: string;
  isActive: boolean;
}

interface ProductionOrder {
  id: string;
  orderNumber: string;
  productName: string;
  quantity: number;
  completedQuantity: number;
  startDate: string;
  expectedCompletion: string;
  actualCompletion?: string;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'urgent';
}

interface Worker {
  id: string;
  name: string;
  role: string;
  isActive: boolean;
  assignedLine?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`production-tabpanel-${index}`}
      aria-labelledby={`production-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const ProductionLineManagement: React.FC = () => {
  const { hasPermission } = useRbac();
  const [tabValue, setTabValue] = useState(0);
  const [productionLines, setProductionLines] = useState<ProductionLine[]>([]);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [productionOrders, setProductionOrders] = useState<ProductionOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState<ProductionLine | Machine | null>(null);
  const [dialogType, setDialogType] = useState<'line' | 'machine' | 'worker'>('line');

  // Form states
  const [lineForm, setLineForm] = useState({
    name: '',
    type: 'fabric' as 'fabric' | 'furniture' | 'metalworks' | 'shoes',
    capacity: 0,
    managerId: '',
    location: '',
    description: '',
  });

  const [machineForm, setMachineForm] = useState({
    name: '',
    model: '',
    type: '',
    capacity: 0,
    lineId: '',
  });

  const [workerForm, setWorkerForm] = useState({
    name: '',
    role: '',
    assignedLine: '',
  });

  // Mock data
  useEffect(() => {
    setProductionLines([
      {
        id: '1',
        name: 'Fabric Line A',
        type: 'fabric',
        status: 'active',
        capacity: 1000,
        currentCapacity: 750,
        efficiency: 85,
        managerId: '1',
        managerName: 'John Manager',
        location: 'Building A, Floor 1',
        description: 'High-capacity fabric production line',
        isActive: true,
        createdAt: '2024-01-01T00:00:00Z',
        lastMaintenance: '2024-01-10T00:00:00Z',
        nextMaintenance: '2024-01-25T00:00:00Z',
        machines: [
          {
            id: '1',
            name: 'Weaving Machine 1',
            model: 'WM-2024',
            type: 'weaving',
            status: 'operational',
            capacity: 500,
            efficiency: 90,
            lastMaintenance: '2024-01-10T00:00:00Z',
            nextMaintenance: '2024-01-25T00:00:00Z',
            isActive: true,
          },
        ],
        currentOrder: {
          id: '1',
          orderNumber: 'ORD-2024-001',
          productName: 'Cotton Fabric',
          quantity: 1000,
          completedQuantity: 750,
          startDate: '2024-01-15T08:00:00Z',
          expectedCompletion: '2024-01-20T17:00:00Z',
          status: 'in_progress',
          priority: 'high',
        },
      },
      {
        id: '2',
        name: 'Furniture Line B',
        type: 'furniture',
        status: 'active',
        capacity: 50,
        currentCapacity: 30,
        efficiency: 78,
        managerId: '2',
        managerName: 'Jane Supervisor',
        location: 'Building B, Floor 2',
        description: 'Custom furniture production line',
        isActive: true,
        createdAt: '2024-01-02T00:00:00Z',
        lastMaintenance: '2024-01-12T00:00:00Z',
        nextMaintenance: '2024-01-27T00:00:00Z',
        machines: [
          {
            id: '2',
            name: 'Wood Cutting Machine',
            model: 'WCM-2024',
            type: 'cutting',
            status: 'operational',
            capacity: 25,
            efficiency: 85,
            lastMaintenance: '2024-01-12T00:00:00Z',
            nextMaintenance: '2024-01-27T00:00:00Z',
            isActive: true,
          },
        ],
        currentOrder: {
          id: '2',
          orderNumber: 'ORD-2024-002',
          productName: 'Oak Table',
          quantity: 50,
          completedQuantity: 30,
          startDate: '2024-01-14T09:00:00Z',
          expectedCompletion: '2024-01-22T17:00:00Z',
          status: 'in_progress',
          priority: 'medium',
        },
      },
    ]);

    setWorkers([
      {
        id: '1',
        name: 'John Manager',
        role: 'Line Manager',
        isActive: true,
        assignedLine: '1',
      },
      {
        id: '2',
        name: 'Jane Supervisor',
        role: 'Supervisor',
        isActive: true,
        assignedLine: '2',
      },
      {
        id: '3',
        name: 'Bob Worker',
        role: 'Operator',
        isActive: true,
        assignedLine: '1',
      },
    ]);
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleOpenDialog = (type: 'line' | 'machine' | 'worker', item?: any) => {
    setDialogType(type);
    setEditingItem(item || null);
    setOpenDialog(true);
    setError(null);

    if (item) {
      if (type === 'line') {
        setLineForm({
          name: item.name,
          type: item.type,
          capacity: item.capacity,
          managerId: item.managerId,
          location: item.location,
          description: item.description,
        });
      } else if (type === 'machine') {
        setMachineForm({
          name: item.name,
          model: item.model,
          type: item.type,
          capacity: item.capacity,
          lineId: item.lineId || '',
        });
      } else if (type === 'worker') {
        setWorkerForm({
          name: item.name,
          role: item.role,
          assignedLine: item.assignedLine || '',
        });
      }
    } else {
      // Reset forms for new items
      if (type === 'line') {
        setLineForm({
          name: '',
          type: 'fabric',
          capacity: 0,
          managerId: '',
          location: '',
          description: '',
        });
      } else if (type === 'machine') {
        setMachineForm({
          name: '',
          model: '',
          type: '',
          capacity: 0,
          lineId: '',
        });
      } else if (type === 'worker') {
        setWorkerForm({
          name: '',
          role: '',
          assignedLine: '',
        });
      }
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!hasPermission('manager:production')) {
      setError('You do not have permission to manage production');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (dialogType === 'line') {
        if (!lineForm.name || !lineForm.type || lineForm.capacity === 0) {
          setError('Please fill in all required fields');
          return;
        }

        if (editingItem) {
          // Update production line
          const updatedLines = productionLines.map(line =>
            line.id === editingItem.id
              ? {
                  ...line,
                  name: lineForm.name,
                  type: lineForm.type,
                  capacity: lineForm.capacity,
                  managerId: lineForm.managerId,
                  location: lineForm.location,
                  description: lineForm.description,
                }
              : line
          );
          setProductionLines(updatedLines);
        } else {
          // Create production line
          const manager = workers.find(w => w.id === lineForm.managerId);
          const newLine: ProductionLine = {
            id: Date.now().toString(),
            name: lineForm.name,
            type: lineForm.type,
            status: 'active',
            capacity: lineForm.capacity,
            currentCapacity: 0,
            efficiency: 0,
            managerId: lineForm.managerId,
            managerName: manager?.name || '',
            location: lineForm.location,
            description: lineForm.description,
            isActive: true,
            createdAt: new Date().toISOString(),
            lastMaintenance: new Date().toISOString(),
            nextMaintenance: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString(),
            machines: [],
          };
          setProductionLines([...productionLines, newLine]);
        }
      } else if (dialogType === 'machine') {
        if (!machineForm.name || !machineForm.model || !machineForm.type) {
          setError('Please fill in all required fields');
          return;
        }

        if (editingItem) {
          // Update machine
          const updatedMachines = machines.map(machine =>
            machine.id === editingItem.id
              ? { ...machine, ...machineForm }
              : machine
          );
          setMachines(updatedMachines);
        } else {
          // Create machine
          const newMachine: Machine = {
            id: Date.now().toString(),
            name: machineForm.name,
            model: machineForm.model,
            type: machineForm.type,
            status: 'operational',
            capacity: machineForm.capacity,
            efficiency: 0,
            lastMaintenance: new Date().toISOString(),
            nextMaintenance: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString(),
            isActive: true,
          };
          setMachines([...machines, newMachine]);
        }
      } else if (dialogType === 'worker') {
        if (!workerForm.name || !workerForm.role) {
          setError('Please fill in all required fields');
          return;
        }

        if (editingItem) {
          // Update worker
          const updatedWorkers = workers.map(worker =>
            worker.id === editingItem.id
              ? { ...worker, ...workerForm }
              : worker
          );
          setWorkers(updatedWorkers);
        } else {
          // Create worker
          const newWorker: Worker = {
            id: Date.now().toString(),
            name: workerForm.name,
            role: workerForm.role,
            isActive: true,
            assignedLine: workerForm.assignedLine || undefined,
          };
          setWorkers([...workers, newWorker]);
        }
      }

      handleCloseDialog();
    } catch (err) {
      setError('Failed to save item');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (type: 'line' | 'machine' | 'worker', id: string) => {
    if (!hasPermission('manager:production')) {
      setError('You do not have permission to delete items');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (type === 'line') {
        setProductionLines(productionLines.filter(line => line.id !== id));
      } else if (type === 'machine') {
        setMachines(machines.filter(machine => machine.id !== id));
      } else if (type === 'worker') {
        setWorkers(workers.filter(worker => worker.id !== id));
      }
    } catch (err) {
      setError('Failed to delete item');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (lineId: string, newStatus: ProductionLine['status']) => {
    if (!hasPermission('manager:production')) {
      setError('You do not have permission to update production lines');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const updatedLines = productionLines.map(line =>
        line.id === lineId
          ? {
              ...line,
              status: newStatus,
            }
          : line
      );
      setProductionLines(updatedLines);
    } catch (err) {
      setError('Failed to update production line status');
    } finally {
      setLoading(false);
    }
  };

  const filteredLines = productionLines.filter(line => {
    const matchesSearch = 
      line.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      line.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = typeFilter === 'all' || line.type === typeFilter;
    const matchesStatus = statusFilter === 'all' || line.status === statusFilter;

    return matchesSearch && matchesType && matchesStatus;
  });

  const stats = {
    totalLines: productionLines.length,
    activeLines: productionLines.filter(l => l.status === 'active').length,
    maintenanceLines: productionLines.filter(l => l.status === 'maintenance').length,
    breakdownLines: productionLines.filter(l => l.status === 'breakdown').length,
    totalMachines: machines.length,
    operationalMachines: machines.filter(m => m.status === 'operational').length,
    totalWorkers: workers.length,
    assignedWorkers: workers.filter(w => w.assignedLine).length,
    averageEfficiency: productionLines.length > 0 
      ? productionLines.reduce((sum, l) => sum + l.efficiency, 0) / productionLines.length 
      : 0,
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'default';
      case 'maintenance':
        return 'warning';
      case 'breakdown':
        return 'error';
      default:
        return 'default';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'fabric':
        return 'primary';
      case 'furniture':
        return 'secondary';
      case 'metalworks':
        return 'warning';
      case 'shoes':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Production Line Management
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Lines
              </Typography>
              <Typography variant="h4">{stats.totalLines}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active Lines
              </Typography>
              <Typography variant="h4" color="success.main">
                {stats.activeLines}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                In Maintenance
              </Typography>
              <Typography variant="h4" color="warning.main">
                {stats.maintenanceLines}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Breakdown
              </Typography>
              <Typography variant="h4" color="error.main">
                {stats.breakdownLines}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Avg Efficiency
              </Typography>
              <Typography variant="h4" color="info.main">
                {stats.averageEfficiency.toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Workers
              </Typography>
              <Typography variant="h4" color="primary.main">
                {stats.totalWorkers}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ width: '100%' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="production tabs">
          <Tab label="Production Lines" />
          <Tab label="Machines" />
          <Tab label="Workers" />
          <Tab label="Analytics" />
        </Tabs>

        {/* Filters */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  label="Type"
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="fabric">Fabric</MenuItem>
                  <MenuItem value="furniture">Furniture</MenuItem>
                  <MenuItem value="metalworks">Metalworks</MenuItem>
                  <MenuItem value="shoes">Shoes</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="maintenance">Maintenance</MenuItem>
                  <MenuItem value="breakdown">Breakdown</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: 'right' }}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => window.location.reload()}
                sx={{ mr: 1 }}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleOpenDialog('line')}
                disabled={!hasPermission('manager:production')}
              >
                Add Line
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Production Lines Tab */}
        <TabPanel value={tabValue} index={0}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Line Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Capacity</TableCell>
                  <TableCell>Efficiency</TableCell>
                  <TableCell>Manager</TableCell>
                  <TableCell>Current Order</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredLines.map((line) => (
                  <TableRow key={line.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {line.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {line.location}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={line.type}
                        color={getTypeColor(line.type) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={line.status}
                        color={getStatusColor(line.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {line.currentCapacity} / {line.capacity}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={(line.currentCapacity / line.capacity) * 100}
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ mr: 1 }}>
                          {line.efficiency}%
                        </Typography>
                        {line.efficiency >= 80 ? (
                          <TrendingUp color="success" fontSize="small" />
                        ) : line.efficiency >= 60 ? (
                          <TrendingDown color="warning" fontSize="small" />
                        ) : (
                          <TrendingDown color="error" fontSize="small" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {line.managerName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {line.currentOrder ? (
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {line.currentOrder.orderNumber}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {line.currentOrder.completedQuantity} / {line.currentOrder.quantity}
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={(line.currentOrder.completedQuantity / line.currentOrder.quantity) * 100}
                            sx={{ mt: 0.5 }}
                          />
                        </Box>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          No active order
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View Details">
                          <IconButton size="small">
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Line">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog('line', line)}
                            disabled={!hasPermission('manager:production')}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Toggle Status">
                          <IconButton
                            size="small"
                            onClick={() => {
                              const nextStatus = line.status === 'active' ? 'maintenance' :
                                line.status === 'maintenance' ? 'active' : 'active';
                              handleStatusChange(line.id, nextStatus);
                            }}
                            disabled={!hasPermission('manager:production')}
                          >
                            {line.status === 'active' ? <Pause /> : <PlayArrow />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Line">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete('line', line.id)}
                            disabled={!hasPermission('manager:production')}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Machines Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ mb: 2, textAlign: 'right' }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog('machine')}
              disabled={!hasPermission('manager:production')}
            >
              Add Machine
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Machine Name</TableCell>
                  <TableCell>Model</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Capacity</TableCell>
                  <TableCell>Efficiency</TableCell>
                  <TableCell>Next Maintenance</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {machines.map((machine) => (
                  <TableRow key={machine.id}>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {machine.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {machine.model}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={machine.type} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={machine.status}
                        color={machine.status === 'operational' ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {machine.capacity}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ mr: 1 }}>
                          {machine.efficiency}%
                        </Typography>
                        {machine.efficiency >= 80 ? (
                          <TrendingUp color="success" fontSize="small" />
                        ) : (
                          <TrendingDown color="warning" fontSize="small" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(machine.nextMaintenance).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Edit Machine">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog('machine', machine)}
                            disabled={!hasPermission('manager:production')}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Machine">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete('machine', machine.id)}
                            disabled={!hasPermission('manager:production')}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Workers Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ mb: 2, textAlign: 'right' }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog('worker')}
              disabled={!hasPermission('manager:production')}
            >
              Add Worker
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Assigned Line</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {workers.map((worker) => (
                  <TableRow key={worker.id}>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {worker.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={worker.role} size="small" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {worker.assignedLine ? 
                          productionLines.find(l => l.id === worker.assignedLine)?.name || 'Unknown' :
                          'Not assigned'
                        }
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={worker.isActive ? 'Active' : 'Inactive'}
                        color={worker.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Edit Worker">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog('worker', worker)}
                            disabled={!hasPermission('manager:production')}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Worker">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete('worker', worker.id)}
                            disabled={!hasPermission('manager:production')}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Analytics Tab */}
        <TabPanel value={tabValue} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Production Line Efficiency" />
                <CardContent>
                  {productionLines.map(line => (
                    <Box key={line.id} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">
                          {line.name}
                        </Typography>
                        <Typography variant="body2">
                          {line.efficiency}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={line.efficiency}
                        color={line.efficiency >= 80 ? 'success' : line.efficiency >= 60 ? 'warning' : 'error'}
                      />
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Production Line Status" />
                <CardContent>
                  {['active', 'maintenance', 'breakdown', 'inactive'].map(status => {
                    const count = productionLines.filter(l => l.status === status).length;
                    const percentage = productionLines.length > 0 ? (count / productionLines.length) * 100 : 0;
                    return (
                      <Box key={status} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                            {status}
                          </Typography>
                          <Typography variant="body2">
                            {count} ({percentage.toFixed(1)}%)
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={percentage}
                          color={getStatusColor(status) as any}
                        />
                      </Box>
                    );
                  })}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>

      {/* Dialogs */}
      {/* Production Line Dialog */}
      <Dialog open={openDialog && dialogType === 'line'} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingItem ? 'Edit Production Line' : 'Add New Production Line'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Line Name"
                value={lineForm.name}
                onChange={(e) => setLineForm({ ...lineForm, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={lineForm.type}
                  onChange={(e) => setLineForm({ ...lineForm, type: e.target.value as any })}
                  label="Type"
                  required
                >
                  <MenuItem value="fabric">Fabric</MenuItem>
                  <MenuItem value="furniture">Furniture</MenuItem>
                  <MenuItem value="metalworks">Metalworks</MenuItem>
                  <MenuItem value="shoes">Shoes</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Capacity"
                type="number"
                value={lineForm.capacity}
                onChange={(e) => setLineForm({ ...lineForm, capacity: parseInt(e.target.value) })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Manager</InputLabel>
                <Select
                  value={lineForm.managerId}
                  onChange={(e) => setLineForm({ ...lineForm, managerId: e.target.value })}
                  label="Manager"
                >
                  {workers.filter(w => w.role.includes('Manager') || w.role.includes('Supervisor')).map(worker => (
                    <MenuItem key={worker.id} value={worker.id}>
                      {worker.name} ({worker.role})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Location"
                value={lineForm.location}
                onChange={(e) => setLineForm({ ...lineForm, location: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={lineForm.description}
                onChange={(e) => setLineForm({ ...lineForm, description: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Saving...' : (editingItem ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Machine Dialog */}
      <Dialog open={openDialog && dialogType === 'machine'} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingItem ? 'Edit Machine' : 'Add New Machine'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Machine Name"
                value={machineForm.name}
                onChange={(e) => setMachineForm({ ...machineForm, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Model"
                value={machineForm.model}
                onChange={(e) => setMachineForm({ ...machineForm, model: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Type"
                value={machineForm.type}
                onChange={(e) => setMachineForm({ ...machineForm, type: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Capacity"
                type="number"
                value={machineForm.capacity}
                onChange={(e) => setMachineForm({ ...machineForm, capacity: parseInt(e.target.value) })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Production Line</InputLabel>
                <Select
                  value={machineForm.lineId}
                  onChange={(e) => setMachineForm({ ...machineForm, lineId: e.target.value })}
                  label="Production Line"
                >
                  {productionLines.map(line => (
                    <MenuItem key={line.id} value={line.id}>
                      {line.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Saving...' : (editingItem ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Worker Dialog */}
      <Dialog open={openDialog && dialogType === 'worker'} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingItem ? 'Edit Worker' : 'Add New Worker'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                value={workerForm.name}
                onChange={(e) => setWorkerForm({ ...workerForm, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={workerForm.role}
                  onChange={(e) => setWorkerForm({ ...workerForm, role: e.target.value })}
                  label="Role"
                  required
                >
                  <MenuItem value="Line Manager">Line Manager</MenuItem>
                  <MenuItem value="Supervisor">Supervisor</MenuItem>
                  <MenuItem value="Operator">Operator</MenuItem>
                  <MenuItem value="Technician">Technician</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Assigned Line</InputLabel>
                <Select
                  value={workerForm.assignedLine}
                  onChange={(e) => setWorkerForm({ ...workerForm, assignedLine: e.target.value })}
                  label="Assigned Line"
                >
                  <MenuItem value="">Not assigned</MenuItem>
                  {productionLines.map(line => (
                    <MenuItem key={line.id} value={line.id}>
                      {line.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Saving...' : (editingItem ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="add production line"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={() => handleOpenDialog('line')}
        disabled={!hasPermission('manager:production')}
      >
        <Add />
      </Fab>
    </Box>
  );
};
